import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import QRCode from 'react-qr-code';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import {
    checkVerifyStatus,
    createDemoCredentials,
    demoCredentialSelector,
    verifySelector,
    verifyStatusSelector,
    resetVerify,
    resetVerifyStatus,
    setPidJson,
    setToastMessage,
    startVerify,
    useAppDispatch,
} from '../../state';

const PID_VERIFIER_TEMPLATE_KEY = 'request_pid_for_demo_credentials';
const PID_POLL_INTERVAL_MS = 2000;
const PID_VERIFIED_STATUS = 'authorization_response_verified';
const PID_ERROR_STATUS = 'error';

const parseJson = (s: string): Record<string, unknown> | undefined => {
    try {
        const parsed = JSON.parse(s);
        return typeof parsed === 'object' && parsed !== null ? parsed as Record<string, unknown> : undefined;
    } catch {
        return undefined;
    }
};

export const DemoCredentialsForm: FC<{ className?: string }> = ({className}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const auth = useAuth();
    const jwtToken = auth.user?.access_token;
    const state = useSelector(demoCredentialSelector);

    const [error, setError] = useState<string | undefined>(undefined);
    const [kvkNumber, setKvkNumber] = useState('87654321');
    const [companyName, setCompanyName] = useState('Apex Logistics');
    const [address, setAddress] = useState('Tolnasingel 3, 2411 PV Bodegraven');

    const verifyState = useSelector(verifySelector);
    const verifyStatusState = useSelector(verifyStatusSelector);
    const requestUri = verifyState.singleItem?.requestUri;
    const correlationId = verifyState.singleItem?.correlationId;
    const verifyStatus = verifyStatusState.singleItem?.status;
    const verifyClaims = verifyStatusState.singleItem?.claims;
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const appliedClaimsRef = useRef<string | undefined>(undefined);

    const stopPidPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const startPidScan = () => {
        stopPidPolling();
        appliedClaimsRef.current = undefined;
        dispatch(resetVerify());
        dispatch(resetVerifyStatus());
        dispatch(startVerify({jwtToken, verifierTemplateKey: PID_VERIFIER_TEMPLATE_KEY}));
    };

    useEffect(() => {
        if (jwtToken && !requestUri && !correlationId) {
            startPidScan();
        }
    }, [jwtToken]);

    useEffect(() => {
        if (!correlationId) return;
        if (verifyStatus === PID_VERIFIED_STATUS || verifyStatus === PID_ERROR_STATUS) {
            stopPidPolling();
            return;
        }
        if (pollRef.current) return;
        pollRef.current = setInterval(() => {
            dispatch(checkVerifyStatus({jwtToken, sessionId: correlationId}));
        }, PID_POLL_INTERVAL_MS);
        return () => stopPidPolling();
    }, [correlationId, verifyStatus, jwtToken]);

    useEffect(() => () => stopPidPolling(), []);

    useEffect(() => {
        if (verifyStatus !== PID_VERIFIED_STATUS || !verifyClaims) return;
        if (correlationId && appliedClaimsRef.current === correlationId) return;
        dispatch(setPidJson(JSON.stringify(verifyClaims, null, 2)));
        appliedClaimsRef.current = correlationId;
    }, [verifyStatus, verifyClaims, correlationId]);

    const pid = useMemo(() => parseJson(state.pidJson), [state.pidJson]);
    const pidValid = pid !== undefined;

    const onCreate = async () => {
        if (!pid) return;
        try {
            const created = await dispatch(
                createDemoCredentials({
                    jwtToken,
                    request: {pid, kvkNumber, companyName, address},
                }),
            ).unwrap();
            dispatch(setToastMessage({ message: `Created: ${created.join(', ')}` }));
            setError(undefined);
            navigate('/demo-credentials-list');
        } catch (e: unknown) {
            setError((e as Error).message);
        }
    };

    return (
        <div className={className}>
            <h2>Create demo credentials</h2>

            <label htmlFor="pidJson">PID JSON (scan or paste)</label>
            <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-start'}}>
                <div style={{flex: 1, minWidth: 0}}>
                    <InputTextarea
                        id="pidJson"
                        rows={12}
                        style={{width: '100%', fontFamily: 'monospace'}}
                        value={state.pidJson}
                        onChange={e => dispatch(setPidJson(e.target.value))}
                        aria-invalid={!pidValid}
                        aria-describedby="pidJson-help"
                    />
                    <small id="pidJson-help">
                        {pidValid ? 'Valid JSON' : 'Invalid JSON — fix to enable Create'}
                    </small>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem'}}>
                    {requestUri ? (
                        <div style={{background: '#fff', padding: '0.5rem', border: '1px solid #d1d5db'}}>
                            <QRCode value={requestUri} size={180}/>
                        </div>
                    ) : (
                        <div style={{
                            width: 180, height: 180, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            border: '1px dashed #d1d5db', color: '#6b7280',
                        }}>
                            Preparing QR…
                        </div>
                    )}
                    <small>
                        {verifyStatus === PID_VERIFIED_STATUS
                            ? 'PID received'
                            : verifyStatus === PID_ERROR_STATUS
                                ? 'Scan failed'
                                : `Scan with wallet (${verifyStatus ?? 'waiting'})`}
                    </small>
                    <Button
                        label="New QR"
                        severity="secondary"
                        size="small"
                        onClick={startPidScan}
                    />
                </div>
            </div>

            <div style={{display: 'grid', gap: '0.75rem', marginTop: '1rem'}}>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label htmlFor="kvkNumber">KvK-nummer</label>
                    <InputText
                        id="kvkNumber"
                        value={kvkNumber}
                        onChange={e => setKvkNumber(e.target.value)}
                    />
                </div>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label htmlFor="companyName">Bedrijfsnaam</label>
                    <InputText
                        id="companyName"
                        value={companyName}
                        onChange={e => setCompanyName(e.target.value)}
                    />
                </div>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label htmlFor="address">Adres</label>
                    <InputText
                        id="address"
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                    />
                </div>
            </div>


            <div style={{marginTop: '1rem'}}>
                <Button label="Create" onClick={onCreate} disabled={!pidValid}/>
            </div>

            {error && <div role="alert" style={{color: 'var(--red-500)'}}>{error}</div>}
        </div>
    );
};
